import { Component, Input, inject, OnDestroy, OnInit, ViewChild, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule, MatDrawer } from '@angular/material/sidenav';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest, takeUntil } from 'rxjs';
import { User } from '../../../core/models/auth.model';
import { AuthState, Balance } from '../../../core/models/auth.model';
import * as AuthActions from '../../../core/store/auth/auth.actions';
import * as GameSessionActions from '../../../core/store/game-session/game-session.actions';
import { environment } from '../../../../environments/environment';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { JoinGameDialogComponent } from '../join-game-dialog/join-game-dialog.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { SupportedLanguage } from '../../../core/types/lang.type';
import { langPart, localizedURL } from '../../../core/helpers/navigation.helper';
import { TranslationService } from '../../../core/services/translation.service';
import { HintComponent } from '../../components/hint/hint.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QuizAIModalComponent } from '../../../features/playarea/components/quiz-ai/modal/quiz-ai-modal.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatToolbarModule,
    MatMenuModule,
    MatSidenavModule,
    MatDividerModule,
    TranslatePipe,
    LanguageSelectorComponent,
    HintComponent,
    MatTooltipModule,
    QuizAIModalComponent
  ]
})
export class HeaderComponent implements OnInit, OnDestroy {

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private translationService: TranslationService
  ) {
    this.isInRoom = this.router.url.includes('/playarea/room');
    this.isInQuizAI = this.router.url.includes('/profile/quiz-ai');
  }

  @Input() fullMenu = true;
  @Input() inGame = false;
  @Input() inLobby = false;
  @Input() viewerMode: boolean | null = null;
  @Input() joinedAsPlayer: boolean = false;
  @Input() gameTitle?: string = '';
  @Output() leaveSession = new EventEmitter<void>();
  @ViewChild('drawer') drawer!: MatDrawer;

  public cdn = environment.cdnUrl;

  private readonly store = inject(Store<{ auth: AuthState, gameSession: any }>);
  private destroy$ = new Subject<void>();
  public currentUser$: Observable<User | null> = this.store.select(state => state.auth.user);
  public currentSession$: Observable<any> = this.store.select(state => state.gameSession?.currentSession);
  public currentSessionId: number | null = null;
  public currentLanguage: SupportedLanguage = 'en';
  public version = environment.version;
  public user: User | null = null;
  public isInRoom: boolean = false;
  public isInQuizAI: boolean = false;
  public dynamicItemsLoaded = false;
  public currentBalance$: Observable<Balance | null> = this.store.select(state => state.auth.balance);

  public langPart = '';

  ngOnInit(): void {
    this.dynamicItemsLoaded = false;
    combineLatest([this.currentUser$, this.currentSession$])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([user, session]) => {
      this.user = user;
      this.dynamicItemsLoaded = true;
      if (session) {
        this.currentSessionId = session.id;
      }
    });
    this.translationService.getCurrentLang().pipe(takeUntil(this.destroy$)).subscribe(lang => {
      this.currentLanguage = lang;
      this.langPart = langPart(lang);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLogout(): void {
    this.store.dispatch(GameSessionActions.clearCurrentSession());
    this.store.dispatch(AuthActions.logout());
    this.closeDrawer();
  }

  toggleDrawer() {
    this.drawer?.toggle();
  }

  closeDrawer() {
    this.drawer?.close();
  }

  openJoinGameDialog(): void {
    this.dialog.open(JoinGameDialogComponent, {
      width: '400px',
      disableClose: false
    });
  }

  public onLeaveSession(): void {
    this.leaveSession.emit();
  }

  private debugLog(...args: any[]): void {
    if (environment.debugLog) {
      console.log('[Header]', ...args);
    }
  }
}
